import { invoiceIssueChecksPending } from "../utils/billingPresentation.js";

const checkKey = (invoice) => `${invoice.id}:${invoice.version}`;
export const ISSUE_CHECK_RESULT_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * Complète en arrière-plan le contrôle Consolidation différé des brouillons Facturation.
 * Une seule requête à la fois : le serveur poursuit son calcul même si le navigateur
 * abandonne la requête, donc on évite les doublons au lieu d’annuler.
 * Un résultat réussi est réutilisé pour la même version tant que les données de
 * commandes n’ont pas changé (`revision`) et qu’il a moins de `maxAgeMs` : une simple
 * relecture de la liste (retour sur l’onglet, focus) ne relance pas le contrôle.
 * Passé `maxAgeMs`, l’ancien résultat reste affiché pendant que le contrôle est refait.
 * L’émission revérifie toujours côté serveur.
 */
export function createInvoiceIssueChecksCoordinator({
  fetchChecks,
  onResults,
  batchSize = 10,
  revision = () => 0,
  now = () => Date.now(),
  maxAgeMs = ISSUE_CHECK_RESULT_MAX_AGE_MS,
}) {
  let generation = 0;
  let wanted = new Map();
  let inFlight = new Set();
  let running = false;
  let known = new Map();

  const remember = (entry, result, checkedRevision, checkedAt) => {
    if (result.failed || String(result.version) !== String(entry.version)) return;
    known.set(entry.key, { result, revision: checkedRevision, at: checkedAt });
  };
  const recalled = (key) => {
    const hit = known.get(key);
    if (!hit) return null;
    if (hit.revision !== revision()) {
      known.delete(key);
      return null;
    }
    return { result: hit.result, expired: now() - hit.at >= maxAgeMs };
  };

  const nextBatch = () => [...wanted.values()].filter((entry) => !inFlight.has(entry.key)).slice(0, batchSize);

  async function pump() {
    if (running) return;
    running = true;
    const startedGeneration = generation;
    try {
      let batch = nextBatch();
      while (batch.length && generation === startedGeneration) {
        inFlight = new Set(batch.map((entry) => entry.key));
        // Revision read before the request: a change during it must not be hidden.
        const checkedRevision = revision();
        const checkedAt = now();
        let results;
        try {
          const payload = await fetchChecks(batch.map((entry) => entry.id));
          const byId = new Map((Array.isArray(payload?.items) ? payload.items : []).map((item) => [String(item?.id), item]));
          results = batch.map((entry) => {
            const item = byId.get(entry.id);
            return item
              ? { ...item, requested_version: entry.version }
              : { id: entry.id, requested_version: entry.version, failed: true };
          });
        } catch {
          results = batch.map((entry) => ({ id: entry.id, requested_version: entry.version, failed: true }));
        }
        inFlight = new Set();
        if (generation !== startedGeneration) break;
        batch.forEach((entry, index) => {
          wanted.delete(entry.key);
          remember(entry, results[index], checkedRevision, checkedAt);
        });
        onResults(results);
        batch = nextBatch();
      }
    } finally {
      running = false;
      if (generation !== startedGeneration && wanted.size) void pump();
    }
  }

  return {
    /** Suit les brouillons encore à vérifier, dans l’ordre donné (détail ouvert d’abord). */
    sync(invoices) {
      const next = new Map();
      const reused = new Map();
      for (const invoice of Array.isArray(invoices) ? invoices : []) {
        if (!invoice?.id || !invoiceIssueChecksPending(invoice)) continue;
        const key = checkKey(invoice);
        if (next.has(key) || reused.has(key)) continue;
        const hit = recalled(key);
        if (hit) reused.set(key, hit.result);
        if (!hit || hit.expired) next.set(key, { key, id: String(invoice.id), version: invoice.version });
      }
      wanted = next;
      if (reused.size) onResults([...reused.values()]);
      void pump();
    },
    reset() {
      generation += 1;
      wanted = new Map();
      inFlight = new Set();
      known = new Map();
    },
  };
}

// Brouillon en attente, ou brouillon déjà complété qu’un contrôle réussi de la même version rafraîchit.
const acceptsCheck = (invoice, result) => invoiceIssueChecksPending(invoice)
  || (!result.failed && String(result.version) === String(invoice.version)
    && invoiceIssueChecksPending({ ...invoice, issue_checks_pending: true }));

/** Applique les contrôles reçus aux brouillons de la même version. */
export function mergeInvoiceIssueCheckResults(invoices, results) {
  if (!Array.isArray(invoices) || !Array.isArray(results) || !results.length) return invoices;
  const byId = new Map(results.map((result) => [String(result?.id), result]));
  let changed = false;
  const next = invoices.map((invoice) => {
    const result = byId.get(String(invoice?.id));
    if (!result || String(result.requested_version) !== String(invoice.version) || !acceptsCheck(invoice, result)) return invoice;
    changed = true;
    if (result.failed || String(result.version) !== String(invoice.version)) {
      // L’émission revérifie toujours côté serveur : on retombe sur le bouton normal.
      return { ...invoice, issue_checks_pending: false, issue_checks_failed: true };
    }
    return {
      ...invoice,
      can_issue: result.can_issue,
      validation_status: result.validation_status,
      issue_blocking_reasons: result.issue_blocking_reasons,
      issue_notices: result.issue_notices || [],
      issue_checks_pending: false,
      issue_checks_failed: false,
    };
  });
  return changed ? next : invoices;
}
